export type HsRenderOrderStrategy = 'stable-geometry' | 'largest-first' | 'smallest-first' | 'source-order';

export const HS_DEFAULT_RENDER_ORDER_STRATEGY: HsRenderOrderStrategy = 'largest-first';

export interface HsRenderPriorityEntry<T> {
  object: T;
  geometryKey: string;
  objectKey: string;
  radius: number;
}

interface RankedEntry<T> extends HsRenderPriorityEntry<T> {
  priority: number;
  sourceIndex: number;
}

export class HsStableRenderOrder<T> {
  private orderedObjects: T[] | null = null;
  private rankedEntries: RankedEntry<T>[] = [];
  private entriesByObject: Map<T, RankedEntry<T>> = new Map();
  private dirty: boolean = true;
  private rankedStrategy: HsRenderOrderStrategy | null = null;
  private strategy: HsRenderOrderStrategy = HS_DEFAULT_RENDER_ORDER_STRATEGY;

  public setStrategy(strategy: HsRenderOrderStrategy): void {
    if (this.strategy === strategy) {
      return;
    }
    this.strategy = strategy;
    this.invalidate();
  }

  public invalidate(): void {
    this.dirty = true;
  }

  public clear(): void {
    this.orderedObjects = null;
    this.rankedEntries = [];
    this.entriesByObject.clear();
    this.rankedStrategy = null;
    this.dirty = true;
  }

  public get(
    objectCount: number,
    buildEntries: (cachedEntries: ReadonlyMap<T, HsRenderPriorityEntry<T>>) => HsRenderPriorityEntry<T>[]
  ): readonly T[] {
    if (this.orderedObjects !== null && !this.dirty && this.orderedObjects.length === objectCount) {
      return this.orderedObjects;
    }
    const entries: HsRenderPriorityEntry<T>[] = buildEntries(this.entriesByObject);
    const ranked: RankedEntry<T>[] = this.reconcile(entries);
    if (
      this.orderedObjects === null ||
      this.orderedObjects.length !== ranked.length ||
      ranked.some((entry: RankedEntry<T>, index: number) => this.orderedObjects?.[index] !== entry.object)
    ) {
      this.orderedObjects = ranked.map((entry: RankedEntry<T>) => entry.object);
    }
    this.rankedEntries = ranked;
    this.rankedStrategy = this.strategy;
    this.dirty = false;
    return this.orderedObjects;
  }

  private reconcile(entries: HsRenderPriorityEntry<T>[]): RankedEntry<T>[] {
    const repetitions: Map<string, number> = new Map();
    if (this.strategy === 'stable-geometry') {
      for (const entry of entries) {
        repetitions.set(entry.geometryKey, (repetitions.get(entry.geometryKey) ?? 0) + 1);
      }
    }
    const nextByObject: Map<T, RankedEntry<T>> = new Map();
    const changed: RankedEntry<T>[] = [];
    let duplicates: boolean = false;
    for (let sourceIndex: number = 0; sourceIndex < entries.length; sourceIndex++) {
      const entry: HsRenderPriorityEntry<T> = entries[sourceIndex];
      const previous: RankedEntry<T> | undefined = this.entriesByObject.get(entry.object);
      const priority: number = this.getPriority(entry, repetitions);
      const unchanged: boolean =
        this.rankedStrategy === this.strategy &&
        previous !== undefined &&
        previous.geometryKey === entry.geometryKey &&
        previous.objectKey === entry.objectKey &&
        Object.is(previous.radius, entry.radius) &&
        previous.priority === priority;
      const ranked: RankedEntry<T> =
        unchanged && previous !== undefined
          ? previous
          : {
              object: entry.object,
              geometryKey: entry.geometryKey,
              objectKey: entry.objectKey,
              radius: entry.radius,
              priority,
              sourceIndex,
            };
      ranked.sourceIndex = sourceIndex;
      if (!unchanged) {
        changed.push(ranked);
      }
      duplicates = duplicates || nextByObject.has(entry.object);
      nextByObject.set(entry.object, ranked);
    }
    const compare = (left: RankedEntry<T>, right: RankedEntry<T>): number => this.compareEntries(left, right);
    const retained: RankedEntry<T>[] = this.rankedEntries.filter(
      (entry: RankedEntry<T>) => nextByObject.get(entry.object) === entry
    );
    const retainedSorted: boolean = retained.every(
      (entry: RankedEntry<T>, index: number) => index === 0 || compare(retained[index - 1], entry) <= 0
    );
    if (duplicates) {
      const ranked: RankedEntry<T>[] = entries
        .map((entry: HsRenderPriorityEntry<T>, sourceIndex: number) => ({
          object: entry.object,
          geometryKey: entry.geometryKey,
          objectKey: entry.objectKey,
          radius: entry.radius,
          priority: this.getPriority(entry, repetitions),
          sourceIndex,
        }))
        .sort(compare);
      this.entriesByObject = new Map(ranked.map((entry: RankedEntry<T>) => [entry.object, entry]));
      return ranked;
    }
    this.entriesByObject = nextByObject;
    if (!retainedSorted || changed.length > entries.length / 4) {
      return Array.from(nextByObject.values()).sort(compare);
    }
    if (changed.length === 0) {
      return retained;
    }
    changed.sort(compare);
    const merged: RankedEntry<T>[] = [];
    let retainedIndex: number = 0;
    let changedIndex: number = 0;
    while (retainedIndex < retained.length && changedIndex < changed.length) {
      merged.push(
        compare(retained[retainedIndex], changed[changedIndex]) <= 0
          ? retained[retainedIndex++]
          : changed[changedIndex++]
      );
    }
    for (; retainedIndex < retained.length; retainedIndex++) {
      merged.push(retained[retainedIndex]);
    }
    for (; changedIndex < changed.length; changedIndex++) {
      merged.push(changed[changedIndex]);
    }
    return merged;
  }

  private getPriority(entry: HsRenderPriorityEntry<T>, repetitions: ReadonlyMap<string, number>): number {
    if (this.strategy === 'stable-geometry') {
      return Number.isFinite(entry.radius) && entry.radius > 0
        ? Math.log2(repetitions.get(entry.geometryKey) ?? 1) + 2 * Math.log2(entry.radius)
        : -Infinity;
    }
    return Number.isFinite(entry.radius) && entry.radius >= 0 ? entry.radius : -Infinity;
  }

  private compareEntries(left: RankedEntry<T>, right: RankedEntry<T>): number {
    if (this.strategy !== 'source-order' && left.priority !== right.priority) {
      return this.strategy === 'smallest-first' ? left.priority - right.priority : right.priority - left.priority;
    }
    const sourceOrder: number = compareSourceOrder(left, right);
    return sourceOrder !== 0 ? sourceOrder : left.sourceIndex - right.sourceIndex;
  }
}

export function hsOrderByGeometryPriority<T>(entries: HsRenderPriorityEntry<T>[]): T[] {
  const repetitions: Map<string, number> = new Map();
  for (const entry of entries) {
    repetitions.set(entry.geometryKey, (repetitions.get(entry.geometryKey) ?? 0) + 1);
  }

  const ranked: (HsRenderPriorityEntry<T> & { priority: number })[] = entries.map(
    (entry: HsRenderPriorityEntry<T>) => ({
      ...entry,
      priority:
        Number.isFinite(entry.radius) && entry.radius > 0
          ? Math.log2(repetitions.get(entry.geometryKey) ?? 1) + 2 * Math.log2(entry.radius)
          : -Infinity,
    })
  );
  ranked.sort(
    (left: HsRenderPriorityEntry<T> & { priority: number }, right: HsRenderPriorityEntry<T> & { priority: number }) => {
      if (left.priority !== right.priority) {
        return left.priority > right.priority ? -1 : 1;
      }
      if (left.geometryKey !== right.geometryKey) {
        return left.geometryKey < right.geometryKey ? -1 : 1;
      }
      if (left.objectKey !== right.objectKey) {
        return left.objectKey < right.objectKey ? -1 : 1;
      }
      return 0;
    }
  );
  return ranked.map((entry: HsRenderPriorityEntry<T> & { priority: number }) => entry.object);
}

export function hsOrderByRenderStrategy<T>(
  entries: HsRenderPriorityEntry<T>[],
  strategy: HsRenderOrderStrategy = HS_DEFAULT_RENDER_ORDER_STRATEGY
): T[] {
  if (strategy === 'stable-geometry') {
    return hsOrderByGeometryPriority(entries);
  }

  const ranked: HsRenderPriorityEntry<T>[] = entries.slice();
  ranked.sort((left: HsRenderPriorityEntry<T>, right: HsRenderPriorityEntry<T>) => {
    if (strategy === 'source-order') {
      return compareSourceOrder(left, right);
    }

    const leftRadius: number = Number.isFinite(left.radius) && left.radius >= 0 ? left.radius : -Infinity;
    const rightRadius: number = Number.isFinite(right.radius) && right.radius >= 0 ? right.radius : -Infinity;
    if (leftRadius !== rightRadius) {
      return strategy === 'largest-first' ? rightRadius - leftRadius : leftRadius - rightRadius;
    }
    return compareSourceOrder(left, right);
  });
  return ranked.map((entry: HsRenderPriorityEntry<T>) => entry.object);
}

function compareSourceOrder<T>(left: HsRenderPriorityEntry<T>, right: HsRenderPriorityEntry<T>): number {
  if (left.geometryKey !== right.geometryKey) {
    return left.geometryKey < right.geometryKey ? -1 : 1;
  }
  if (left.objectKey !== right.objectKey) {
    return left.objectKey < right.objectKey ? -1 : 1;
  }
  return 0;
}
